import apiService from '../../../reduxs/apiService'
import { MAX_ROOM_LIST_LIMIT, ROOM_STATUS, SPACE_TYPE } from '../../../helpers'

const parseJson = (value) => {
  if (value && typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch (_) {
    return null
  }
}

const getPolygonBox = (coord) => {
  const points = String(coord || '').trim().split(/\s+/)
    .map((pair) => pair.split(',').map(Number))
    .filter(([x, y]) => !Number.isNaN(x) && !Number.isNaN(y))
  if (points.length === 0) return null

  const xs = points.map(([x]) => x)
  const ys = points.map(([, y]) => y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)
  const width = Math.max(...xs) - minX
  const height = Math.max(...ys) - minY
  return { x: minX + width / 2, y: minY + height / 2, width, height }
}

const getDeskBox = (room) => {
  const center = parseJson(room.centerPoint)
  const shape = parseJson(room.shape)
  if (center && shape && shape.width > 0 && shape.height > 0) {
    return { x: Number(center.x), y: Number(center.y), width: Number(shape.width), height: Number(shape.height) }
  }
  return getPolygonBox(room.coord)
}

const normalizeFacilityLabels = (facilities) => {
  if (!Array.isArray(facilities)) return []
  const labels = facilities.map((item) => String((item && item.itemName) || '').trim()).filter(Boolean)
  return [...new Set(labels)]
}

const extractRawRoomList = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data && data.list)) return data.list
  return []
}

const normalizeDesk = (room) => {
  const id = room.roomId || room.id
  const coord = String(room.coord || '').trim()
  const box = coord ? getDeskBox(room) : null
  if (!id || !box) return null

  return {
    id,
    name: String(room.roomName || room.name || '').trim(),
    coord,
    box,
    capacity: room.capacity,
    facilities: normalizeFacilityLabels(room.facilities),
    checkInRequired: Boolean(room.checkInRequired),
    isUsing: Number(room.isUsing) === 1,
    ownerName: room.ownerName || null,
    ownerEmail: room.ownerEmail || null,
  }
}

export const fetchDesks = async (floorId, dateTime = new Date()) => {
  const res = await apiService.get('rest/v2/room/', {
    floorId,
    coord: true,
    spaceType: SPACE_TYPE.DESK,
    status: String(ROOM_STATUS.ACTIVE),
    includeOwner: true,
    limit: MAX_ROOM_LIST_LIMIT,
    dateTimeNow: dateTime.toISOString(),
  })
  if (!res || !res.status) throw new Error('fetchDesks failed')

  return extractRawRoomList(res.data).map(normalizeDesk).filter(Boolean)
}

export const fetchDeskFloorPlan = async (floorId) => {
  const desks = await fetchDesks(floorId)
  if (desks.length === 0) return null

  const floorRes = await apiService.get('rest/v1/getdetail_floor', { floorId })
  const floor = floorRes && floorRes.status ? floorRes.data : null
  if (!floor || !floor.imageBase64) return null

  return {
    floorLevel: floor.floorLevel,
    floorName: floor.floorName,
    buildingName: floor.buildingName,
    imageBase64: floor.imageBase64,
    imageWidth: floor.imageWidth,
    imageHeight: floor.imageHeight,
  }
}
